import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
// Define BudgetContext
import { useAuth } from "../components/AuthProvider";
import { getBudgetState, setBudgetState } from "../services/budgetStore";
import {
  getTransactions,
  addTransaction,
  deleteTransaction,
} from "../services/transactionStore";

const BudgetContext = createContext();

// Helper: Get YYYY-MM string for current month
const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const BudgetProvider = ({ children }) => {
  const { user } = useAuth();
  const [budgetState, setBudgetStateLocal] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Monthly Rollover Logic ---
  const performMonthlyRollover = useCallback(async (current, month) => {
    if (current?.month === month && current?.rolloverCompleted) return current;
    const prevMonth = current?.month || month;
    const categories = (current?.categories || []).map(cat => {
      return {
        ...cat,
        month,
        budgetedAmount: 0,
        activityAmount: 0,
        availableAmount: cat.availableAmount || 0,
      };
    });
    const newState = {
      ...current,
      month,
      rolloverCompleted: true,
      categories,
    };
    await setBudgetState(user.uid, newState);
    return newState;
  }, [user]);

  useEffect(() => {
    if (!user || !user.uid) {
      setLoading(true);
      setBudgetStateLocal(null);
      setTransactions([]);
      return;
    }
    setLoading(true);
    Promise.all([getBudgetState(user.uid), getTransactions(user.uid)])
      .then(async ([budget, txs]) => {
        let state = budget || { tbb: 0, categories: [], month: getCurrentMonth(), rolloverCompleted: false };
        const currentMonth = getCurrentMonth();
        if (state.month !== currentMonth || !state.rolloverCompleted) {
          state = await performMonthlyRollover(state, currentMonth);
        }
        setBudgetStateLocal(state);
        setTransactions(txs);
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }, [user, performMonthlyRollover]);

  const addIncome = useCallback(
    async (amount) => {
      if (!user) return;
      setLoading(true);
      try {
        const current = await getBudgetState(user.uid);
        const newTbb = (current?.tbb || 0) + amount;
        const newState = { ...current, tbb: newTbb };
        await setBudgetState(user.uid, newState);
        await addTransaction(user.uid, {
          amount,
          date: new Date().toISOString().slice(0, 10),
          description: "Income",
          categoryId: "Income-TBB",
        });
        setBudgetStateLocal(newState);
      } catch (err) {
        setError(err);
      }
      setLoading(false);
    },
    [user]
  );

  const allocateFunds = useCallback(
    async (categoryId, amount) => {
      if (!user) return;
      setLoading(true);
      try {
        const current = await getBudgetState(user.uid);
        const categories =
          current?.categories?.map((cat) =>
            cat.id === categoryId
              ? {
                  ...cat,
                  budgetedAmount: (cat.budgetedAmount || 0) + amount,
                  availableAmount: (cat.availableAmount || 0) + amount,
                }
              : cat
          ) || [];
        const newTbb = (current?.tbb || 0) - amount;
        const newState = { ...current, categories, tbb: newTbb };
        await setBudgetState(user.uid, newState);
        setBudgetStateLocal(newState);
      } catch (err) {
        setError(err);
        throw err;
      }
      setLoading(false);
    },
    [user]
  );

  const moveCategoryFund = useCallback(
    async (sourceId, destinationId, amount) => {
      if (!user || amount <= 0 || sourceId === destinationId) return;
      setLoading(true);
      try {
        const current = await getBudgetState(user.uid);
        const categories = current?.categories || [];
        const sourceCat = categories.find((cat) => cat.id === sourceId);
        if (!sourceCat || sourceCat.availableAmount < amount) {
          throw new Error("Source category has insufficient available funds.");
        }
        const updatedCategories = categories.map((cat) => {
          if (cat.id === sourceId) {
            return {
              ...cat,
              budgetedAmount: (cat.budgetedAmount || 0) - amount,
              availableAmount: (cat.availableAmount || 0) - amount,
            };
          } else if (cat.id === destinationId) {
            return {
              ...cat,
              budgetedAmount: (cat.budgetedAmount || 0) + amount,
              availableAmount: (cat.availableAmount || 0) + amount,
            };
          }
          return cat;
        });
        const newState = { ...current, categories: updatedCategories };
        await setBudgetState(user.uid, newState);
        setBudgetStateLocal(newState);
      } catch (err) {
        setError(err);
      }
      setLoading(false);
    },
    [user]
  );

  const recordTransaction = useCallback(
    async (tx) => {
      if (!user) return;
      setLoading(true);
      try {
        await addTransaction(user.uid, tx);
        const budget = await getBudgetState(user.uid);
        let categories = budget?.categories || [];
        if (tx.categoryId) {
          categories = categories.map((cat) =>
            cat.id === tx.categoryId
              ? {
                  ...cat,
                  availableAmount:
                    (cat.availableAmount || 0) + (tx.amount || 0),
                  activityAmount: (cat.activityAmount || 0) + (tx.amount || 0),
                }
              : cat
          );
        }
        const newState = { ...budget, categories };
        await setBudgetState(user.uid, newState);
        setBudgetStateLocal(newState);
        const txs = await getTransactions(user.uid);
        setTransactions(txs);
      } catch (err) {
        setError(err);
      }
      setLoading(false);
    },
    [user]
  );

  const removeTransaction = useCallback(
    async (transactionId) => {
      if (!user) return;
      setLoading(true);
      try {
        const txsBefore = await getTransactions(user.uid);
        const txToDelete = txsBefore.find((tx) => tx.id === transactionId);
        await deleteTransaction(user.uid, transactionId);
        if (txToDelete) {
          const budget = await getBudgetState(user.uid);
          const newTbb =
            txToDelete.categoryId === "Income-TBB"
              ? (budget?.tbb || 0) - (txToDelete.amount || 0)
              : budget?.tbb || 0;
          let categories = budget?.categories || [];
          if (txToDelete.categoryId && txToDelete.categoryId !== "Income-TBB") {
            categories = categories.map((cat) =>
              cat.id === txToDelete.categoryId
                ? {
                    ...cat,
                    availableAmount:
                      (cat.availableAmount || 0) - (txToDelete.amount || 0),
                    activityAmount:
                      (cat.activityAmount || 0) - (txToDelete.amount || 0),
                  }
                : cat
            );
          }
          const newState = { ...budget, tbb: newTbb, categories };
          await setBudgetState(user.uid, newState);
          setBudgetStateLocal(newState);
        }
        const txs = await getTransactions(user.uid);
        setTransactions(txs);
      } catch (err) {
        setError(err);
      }
      setLoading(false);
    },
    [user]
  );

  const addCategory = useCallback(
    async (name) => {
      if (!user) return;
      setLoading(true);
      try {
        const current = await getBudgetState(user.uid);
        const newCat = {
          id: Date.now().toString(),
          name,
          budgetedAmount: 0,
          availableAmount: 0,
          goalAmount: 0,
          activityAmount: 0,
          month: getCurrentMonth(),
        };
        const categories = [...(current?.categories || []), newCat];
        const newState = { ...current, categories };
        await setBudgetState(user.uid, newState);
        setBudgetStateLocal(newState);
      } catch (err) {
        setError(err);
      }
      setLoading(false);
    },
    [user]
  );

  const editCategory = useCallback(
    async (catId, newName) => {
      if (!user) return;
      setLoading(true);
      try {
        const current = await getBudgetState(user.uid);
        const categories = (current?.categories || []).map((cat) =>
          cat.id === catId ? { ...cat, name: newName } : cat
        );
        const newState = { ...current, categories };
        await setBudgetState(user.uid, newState);
        setBudgetStateLocal(newState);
      } catch (err) {
        setError(err);
      }
      setLoading(false);
    },
    [user]
  );

  const deleteCategory = useCallback(
    async (catId) => {
      if (!user) return;
      setLoading(true);
      try {
        const current = await getBudgetState(user.uid);
        const categories = (current?.categories || []).filter(
          (cat) => cat.id !== catId
        );
        const newState = { ...current, categories };
        await setBudgetState(user.uid, newState);
        setBudgetStateLocal(newState);
      } catch (err) {
        setError(err);
      }
      setLoading(false);
    },
    [user]
  );

  return (
    <BudgetContext.Provider
      value={{
        budgetState,
        transactions,
        loading,
        error,
        addIncome,
        allocateFunds,
        moveCategoryFund,
        recordTransaction,
        removeTransaction,
        addCategory,
        editCategory,
        deleteCategory,
        performMonthlyRollover,
      }}
    >
      {children}
    </BudgetContext.Provider>
  );
};

export { BudgetProvider };
export const useBudgetContext = () => useContext(BudgetContext);
